import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MarkdownEditorHandle } from "@/components/editor/MarkdownEditor";

/**
 * In-document find, in the place macOS puts it.
 *
 * A native WebView has no find UI at all, so ⌘F in the Mac app used to do
 * nothing — the sharpest "this is just a webpage" moment in the product. This
 * bar follows the conventions of every Mac text editor: Enter walks forward,
 * Shift+Enter walks back, Escape returns to the text at the current match.
 *
 * Matching is a case-insensitive literal scan, recomputed from the live
 * buffer on every step so edits between steps can never strand an offset.
 */

interface FindBarProps {
  editor: MarkdownEditorHandle | null;
  open: boolean;
  onClose: () => void;
}

function findMatches(haystack: string, needle: string): number[] {
  if (!needle) return [];
  const matches: number[] = [];
  const lowerHaystack = haystack.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let at = lowerHaystack.indexOf(lowerNeedle);
  while (at !== -1 && matches.length < 10_000) {
    matches.push(at);
    at = lowerHaystack.indexOf(lowerNeedle, at + Math.max(1, lowerNeedle.length));
  }
  return matches;
}

export function FindBar({ editor, open, onClose }: FindBarProps) {
  const [query, setQuery] = useState("");
  const [position, setPosition] = useState<{ index: number; total: number }>({
    index: 0,
    total: 0,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  /** Offset of the match currently selected, so stepping continues from it. */
  const lastMatchRef = useRef<number | null>(null);

  /** Select match `index` (already wrapped) out of `matches`. */
  const reveal = useCallback(
    (matches: number[], index: number, length: number) => {
      if (!editor || matches.length === 0) {
        setPosition({ index: 0, total: matches.length });
        return;
      }
      const at = matches[index];
      lastMatchRef.current = at;
      setPosition({ index: index + 1, total: matches.length });
      editor.select(at, at + length, { reveal: true });
    },
    [editor],
  );

  /**
   * Walk to the next/previous match relative to where the last step landed —
   * or, on a fresh search, to the first match at or after the caret.
   */
  const step = useCallback(
    (direction: 1 | -1) => {
      if (!editor) return;
      const matches = findMatches(editor.getMarkdown(), query);
      if (matches.length === 0) {
        setPosition({ index: 0, total: 0 });
        return;
      }

      const from = lastMatchRef.current;
      let index: number;
      if (from === null) {
        const caret = editor.getSelection().start;
        index = matches.findIndex((at) => at >= caret);
        if (index === -1) index = 0;
        if (direction === -1) index = (index - 1 + matches.length) % matches.length;
      } else {
        index = matches.indexOf(from);
        if (index === -1) {
          // The text moved under the match; land on the nearest one instead.
          index = matches.findIndex((at) => at >= from);
          if (index === -1) index = 0;
        } else {
          index = (index + direction + matches.length) % matches.length;
        }
      }
      reveal(matches, index, query.length);
    },
    [editor, query, reveal],
  );

  // ⌘G / ⌘⇧G step through matches from anywhere while the bar is open — the
  // Mac convention, and necessary here because stepping hands focus to the
  // editor (WebKit does not paint a selection in an unfocused textarea, so a
  // match found from the input would be invisible).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key !== "g" && event.key !== "G") return;
      event.preventDefault();
      step(event.shiftKey ? -1 : 1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, step]);

  // A fresh query counts and selects immediately — the count is the feedback
  // that tells you whether to keep typing the word or start walking.
  useEffect(() => {
    if (!open || !editor) return;
    lastMatchRef.current = null;
    const matches = findMatches(editor.getMarkdown(), query);
    if (query && matches.length > 0) {
      const caret = editor.getSelection().start;
      let index = matches.findIndex((at) => at >= caret);
      if (index === -1) index = 0;
      reveal(matches, index, query.length);
      // Selecting focuses the textarea; the author is still typing the query.
      inputRef.current?.focus();
    } else {
      setPosition({ index: 0, total: 0 });
    }
    // `reveal` is stable; rerunning on `open` is what seeds a reopened bar.
  }, [query, open, editor, reveal]);

  // Prefill from the selection, the way ⌘E-less Mac apps do on ⌘F.
  useEffect(() => {
    if (!open || !editor) return;
    const { start, end } = editor.getSelection();
    if (end > start && end - start <= 200) {
      const selected = editor.getMarkdown().slice(start, end);
      if (!selected.includes("\n")) setQuery(selected);
    }
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [open, editor]);

  if (!open) return null;

  return (
    <div className="flex items-center gap-1 border-t border-border bg-card px-4 py-1.5">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Stepping focuses the editor so the match is visibly selected;
            // ⌘G / ⌘⇧G continue the walk from there.
            e.preventDefault();
            step(e.shiftKey ? -1 : 1);
          } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
            editor?.focus();
          }
        }}
        placeholder="Find in document"
        aria-label="Find in document"
        className="h-7 w-56 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
      <span className="w-20 shrink-0 px-1 text-xs tabular-nums text-muted-foreground">
        {query ? (position.total > 0 ? `${position.index} of ${position.total}` : "Not found") : ""}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => step(-1)}
        disabled={position.total === 0}
        title="Previous match (⌘⇧G)"
      >
        <ChevronUp className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => step(1)}
        disabled={position.total === 0}
        title="Next match (⌘G)"
      >
        <ChevronDown className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="ml-auto h-7 w-7"
        onClick={() => {
          onClose();
          editor?.focus();
        }}
        title="Done (Esc)"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
